import cx from 'classnames'

import { DefaultType, Property, PropertyType } from 'api/clients/PropertyClient'
import { Add, Trash } from 'almanac-ui/icons/16'
import { IconButton } from 'components/IconButton/IconButton'
import { Button } from 'components/Button'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useFinderContext } from 'screens/Finder/FinderContext'
import type { Condition, Filters, Group } from '../../Filter'
import { Join, Operator } from '../../constants'
import { FilterOperators } from './components/FilterOperators/FilterOperators'
import { JoinButton } from './components/JoinButton/JoinButton'
import { PropertyFilter } from './components/PropertyFilter/PropertyFilter'
import { ValueCollection } from './components/ValueCollection/ValueCollection'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { defaultConditionForProperty } from '../../utils'
import Styles from './FilterList.module.scss'
import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'
import { ViewType } from 'types'
import { filterGroupsWithoutCalendarProperty } from 'screens/Finder/components/views/CalendarView/utils'
import { capitalize } from 'lodash'

export function isGroup(condition: Group | Condition | undefined): condition is Group {
  if (!condition) return false

  return (condition as Group).glue !== undefined
}

export function isCondition(condition: Group | Condition | undefined): condition is Condition {
  if (!condition) return false

  return (condition as Group).glue === undefined
}

interface Props {
  filters: Filters
  setFilters: (filters: Filters) => void
  onRemovedAllFilters?: () => void
}

const RemoveConditionButton = ({ onPress, isDisabled = false }: { onPress: () => void; isDisabled?: boolean }) => (
  <IconButton onPress={onPress} variant="tertiary" isDisabled={isDisabled} icon={Trash} size="md" />
)

export const FilterList = ({ filters, setFilters, onRemovedAllFilters }: Props) => {
  const { filesView, updateSchema, refresh: reloadFinder } = useFinderContext()
  const { isReadOnlyUser } = useHandbookReadOnlyUser()

  const groups = filterGroupsWithoutCalendarProperty({ filesView, groups: filters?.groups })
  const defaultProperty = {
    default_type: DefaultType.Name,
    name: capitalize(DefaultType.Name),
    type: PropertyType.Title,
  } as Property

  const isCalendarPropertyId = (propertyId: number | undefined) => {
    return filesView?.schema.default_view === ViewType.Calendar && filesView?.schema.calendar_property_id === propertyId
  }

  const updateFilters = async (newFilters?: Filters) => {
    if (newFilters?.groups.length === 0) onRemovedAllFilters?.()

    const res = await updateSchema({ ...filesView?.schema, filters: newFilters || filters })

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      reloadFinder()
      return
    }

    showToastMessage('We were not able to apply your filters', 'error')
  }

  const updateLocalFilters = (localFilters: Filters, updateOnServer: boolean = false) => {
    setFilters(localFilters)

    if (
      (localFilters.groups.length === 0 &&
        filesView?.schema?.filters?.groups &&
        filesView.schema.filters.groups.length > 0) ||
      updateOnServer
    ) {
      // This ensures that we apply the filters when the last one is deleted
      updateFilters(localFilters)
      return
    }
  }

  const updateGlue = (glue: Join, groupIndex: number, conditionIndex?: number, conditionGroupIndex?: number) => {
    if (typeof conditionGroupIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          const newConditions = group.conditions.filter(isGroup).map((condition, internalConditionIndex) => {
            if (internalConditionIndex === conditionIndex) {
              return {
                glue,
                conditions: condition.conditions,
              }
            }
            return condition
          })

          return {
            ...group,
            conditions: newConditions,
          }
        }
        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    if (typeof conditionIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          return {
            ...group,
            glue,
          }
        }

        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    updateLocalFilters({
      ...filters,
      glue,
    })
  }

  const changeOperator = async (
    operator: Operator,
    groupIndex: number,
    conditionIndex?: number,
    nestedConditionIndex?: number,
  ) => {
    if (typeof nestedConditionIndex !== 'undefined' && typeof conditionIndex !== 'undefined') {
      const parentCondition = filters.groups[groupIndex].conditions[conditionIndex]
      const group = isGroup(parentCondition) ? parentCondition : undefined
      const nestedCondition = group?.conditions?.[nestedConditionIndex]

      if (isCondition(nestedCondition)) {
        if (nestedCondition.operator === operator) return
      }

      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          const newConditions = group.conditions.filter(isGroup).map((condition, internalConditionIndex) => {
            if (internalConditionIndex === conditionIndex) {
              const newNestedConditions = condition.conditions.map((nestedCondition, internalNestedConditionIndex) => {
                if (internalNestedConditionIndex === nestedConditionIndex) {
                  return {
                    ...nestedCondition,
                    operator,
                  }
                }
                return nestedCondition
              })
              return {
                ...condition,
                groups: newNestedConditions,
              }
            }
            return condition
          })
          return {
            ...group,
            conditions: newConditions,
          }
        }
        return group
      })

      updateLocalFilters(
        {
          ...filters,
          groups: newGroups,
        },
        true,
      )

      return
    }

    if (typeof conditionIndex !== 'undefined') {
      const parentCondition = filters.groups[groupIndex].conditions[conditionIndex]
      const condition = isCondition(parentCondition) ? parentCondition : undefined

      if (!condition || condition.operator === operator) return

      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex !== groupIndex) return group

        const newConditions: (Condition | Group)[] = group.conditions.map((condition, internalConditionIndex) => {
          if (internalConditionIndex !== conditionIndex) return condition

          return {
            ...condition,
            operator,
          }
        })

        return {
          ...group,
          conditions: newConditions,
        }
      })

      updateLocalFilters(
        {
          ...filters,
          groups: newGroups,
        },
        true,
      )

      return
    }

    const parentCondition = filters.groups[groupIndex].conditions[0]
    const condition = isCondition(parentCondition) ? parentCondition : undefined

    if (!condition || condition.operator === operator) return

    const newGroups = filters.groups.map((group, i) => {
      if (i === groupIndex) {
        const updatedConditions = [
          {
            ...group.conditions[0],
            operator,
          },
        ]

        return {
          ...group,
          conditions: updatedConditions,
        }
      }

      return group
    })

    updateLocalFilters(
      {
        ...filters,
        groups: newGroups,
      },
      true,
    )
  }

  const changePropertyFilter = (
    property: Property,
    groupIndex: number,
    conditionIndex?: number,
    nestedConditionIndex?: number,
  ) => {
    if (typeof nestedConditionIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          const newConditions = group.conditions.filter(isGroup).map((condition, internalConditionIndex) => {
            if (internalConditionIndex === conditionIndex) {
              const newGroups = condition.conditions.map((nestedCondition, internalNestedConditionIndex) => {
                if (internalNestedConditionIndex === nestedConditionIndex) {
                  return defaultConditionForProperty(property)
                }
                return nestedCondition
              })
              return {
                ...condition,
                groups: newGroups,
              }
            }
            return condition
          })
          return {
            ...group,
            conditions: newConditions,
          }
        }
        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    if (typeof conditionIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          const newConditions = group.conditions.map((condition, internalConditionIndex) => {
            if (internalConditionIndex === conditionIndex) {
              return defaultConditionForProperty(property)
            }
            return condition
          })
          return {
            ...group,
            conditions: newConditions,
          }
        }
        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    const newGroups = filters.groups.map((group, internalGroupIndex) => {
      if (groupIndex !== internalGroupIndex) return group

      const newConditions = group.conditions.map((condition, internalConditionIndex) => {
        if (conditionIndex !== internalConditionIndex) return condition

        return defaultConditionForProperty(property)
      })

      return {
        ...group,
        conditions: newConditions,
      }
    })

    updateLocalFilters({
      ...filters,
      groups: newGroups,
    })
  }

  const addCondition = (groupIndex?: number, conditionIndex?: number) => {
    if (!filesView?.schema.properties[0]) return
    let newCondition = defaultConditionForProperty(defaultProperty)

    if (typeof conditionIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          const newConditions = group.conditions.filter(isGroup).map((condition, internalConditionIndex) => {
            if (internalConditionIndex === conditionIndex) {
              return {
                ...condition,
                groups: [...condition.conditions, newCondition],
              }
            }
            return condition
          })

          return {
            ...group,
            conditions: newConditions,
          }
        }
        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    if (typeof groupIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          return {
            ...group,
            conditions: [...group.conditions, newCondition],
          }
        }
        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    updateLocalFilters({
      ...filters,
      groups: [...filters.groups, { glue: Join.And, conditions: [newCondition] }],
    })
  }

  const removeCondition = async (groupIndex: number, conditionIndex?: number, nestedConditionIndex?: number) => {
    if (typeof nestedConditionIndex !== 'undefined') {
      const newGroups = filters.groups
        .map((group, internalGroupIndex) => {
          if (internalGroupIndex === groupIndex) {
            const conditions = group.conditions
              .filter(isGroup)
              .map((condition, internalConditionIndex) => {
                if (internalConditionIndex === conditionIndex) {
                  const newNestedConditions = condition.conditions.filter(
                    (nestedCondition, internalNestedConditionIndex) =>
                      internalNestedConditionIndex !== nestedConditionIndex,
                  )

                  if (newNestedConditions.length) {
                    return {
                      ...condition,
                      groups: newNestedConditions,
                    }
                  }

                  return {}
                }

                return condition
              })
              .filter((c) => Object.keys(c).length > 0)

            if (conditions?.length) {
              return {
                glue: group.glue,
                conditions,
              }
            }

            return {}
          }

          return group
        })
        .filter((ng) => Object.keys(ng).length > 0)

      updateFilters({
        ...filters,
        groups: newGroups as Group[],
      })

      return
    }

    if (typeof conditionIndex !== 'undefined') {
      const newGroups = filters.groups
        .map((group, internalGroupIndex) => {
          if (internalGroupIndex === groupIndex) {
            const conditions = group.conditions.filter(
              (condition, internalConditionIndex) => internalConditionIndex !== conditionIndex,
            )

            if (conditions?.length) {
              return {
                glue: group.glue,
                conditions,
              }
            }

            return {}
          }

          return group
        })
        .filter((nG) => Object.keys(nG).length > 0)

      updateFilters({
        ...filters,
        groups: newGroups as Group[],
      })

      return
    }

    updateFilters({
      ...filters,
      groups: filters.groups.filter((group, internalGroupIndex) => internalGroupIndex !== groupIndex),
    })
  }

  /* Will bring back when allowing nested conditions
  const addConditionGroup = (groupIndex?: number) => {
    if (typeof groupIndex !== 'undefined') {
      const newGroups = filters.groups.map((group, internalGroupIndex) => {
        if (internalGroupIndex === groupIndex) {
          return {
            ...group,
            conditions: [
              ...group.conditions,
              {
                glue: Join.And,
                groups: [],
              }
            ]
          }
        }

        return group
      })

      updateLocalFilters({
        ...filters,
        groups: newGroups,
      })

      return
    }

    updateLocalFilters({
      ...filters,
      groups: [
        ...filters.groups,
        {
          glue: Join.And,
          conditions: [],
        }
      ]
    })
  }
  */

  const updateValue = async (value: any, groupIndex: number, conditionIndex: number, conditionGroupIndex?: number) => {
    const newGroups = filters.groups.map((group, internalGroupIndex) => {
      if (internalGroupIndex === groupIndex) {
        const newConditions = group.conditions.map((condition, internalConditionIndex) => {
          if (internalConditionIndex === conditionIndex) {
            return {
              ...condition,
              value: value,
            }
          }

          return condition
        })

        return {
          ...group,
          conditions: newConditions,
        }
      }

      return group
    })

    updateLocalFilters(
      {
        ...filters,
        groups: newGroups,
      },
      true,
    )
  }

  return (
    <div className={Styles.menu}>
      <div className={Styles.filterList}>
        {groups?.map((group, index) => {
          const groupIndex = filesView?.schema.default_view === ViewType.Calendar ? index + 1 : index

          if (group.conditions.length === 1 && isCondition(group.conditions[0])) {
            const condition = group.conditions[0]

            return (
              <div className={Styles.filter} data-testid={`filter-${groupIndex}`} key={`group-${groupIndex}`}>
                <JoinButton
                  glue={filters.glue}
                  isWhere={groupIndex === 0}
                  isButton={groupIndex === 1}
                  onSelect={(glue) => updateGlue(glue, groupIndex)}
                  isDisabled={isReadOnlyUser}
                />
                <PropertyFilter
                  condition={condition}
                  onChange={(property: Property) => changePropertyFilter(property, groupIndex, 0)}
                />
                <FilterOperators
                  condition={condition}
                  onChangeOperator={(operator) => changeOperator(operator, groupIndex)}
                />
                <ValueCollection
                  condition={condition}
                  updateValue={(value) => updateValue(value, groupIndex, 0)}
                  onChangeOperator={(operator) => changeOperator(operator, groupIndex)}
                />
                <RemoveConditionButton
                  onPress={() => removeCondition(groupIndex)}
                  isDisabled={isReadOnlyUser || isCalendarPropertyId(condition.operand.property_id)}
                />
              </div>
            )
          }

          return (
            <div
              className={Styles.filterConditions}
              data-testid={`filter-${groupIndex}`}
              key={`filterConditions-${groupIndex}`}
            >
              <JoinButton
                glue={filters.glue}
                isWhere={groupIndex === 0}
                isButton={groupIndex === 1}
                onSelect={(glue) => updateGlue(glue, groupIndex)}
                isDisabled={isReadOnlyUser}
              />
              <div className={Styles.filterConditionsList}>
                <div className={Styles.groupControls}>
                  <div>All of the following are true</div>
                  <div>
                    <DropdownTrigger>
                      <IconButton className={Styles.plusButton} variant="tertiary" icon={Add} />
                      {(close) => (
                        <div className={Styles.addConditionDropdown}>
                          <Button
                            onPress={() => {
                              addCondition(groupIndex)
                              close()
                            }}
                            variant="tertiary"
                          >
                            Add condition
                          </Button>
                          {/* <Button
                            onPress={() => {
                              addConditionGroup(groupIndex)
                              close()
                            }}
                            variant="tertiary"
                          >Add condition group</Button> */}
                        </div>
                      )}
                    </DropdownTrigger>
                    <RemoveConditionButton onPress={() => removeCondition(groupIndex)} isDisabled={isReadOnlyUser} />
                  </div>
                </div>
                {group.conditions.map((condition, conditionIndex) => {
                  if (isCondition(condition)) {
                    return (
                      <div className={Styles.filter} key={`filterConditions-${groupIndex}-${conditionIndex}`}>
                        <JoinButton
                          glue={group.glue}
                          isWhere={conditionIndex === 0}
                          isButton={conditionIndex === 1}
                          onSelect={(glue) => updateGlue(glue, groupIndex, conditionIndex)}
                          isDisabled={isReadOnlyUser}
                        />
                        <PropertyFilter
                          condition={condition}
                          onChange={(property: Property) => changePropertyFilter(property, groupIndex, conditionIndex)}
                        />
                        <FilterOperators
                          condition={condition}
                          onChangeOperator={(operator) => changeOperator(operator, groupIndex, conditionIndex)}
                        />
                        <ValueCollection
                          condition={condition}
                          updateValue={(value) => updateValue(value, groupIndex, conditionIndex)}
                          onChangeOperator={(operator) => changeOperator(operator, groupIndex, conditionIndex)}
                        />
                        <RemoveConditionButton
                          onPress={() => removeCondition(groupIndex, conditionIndex)}
                          isDisabled={isReadOnlyUser || isCalendarPropertyId(condition.operand.property_id)}
                        />
                      </div>
                    )
                  } else {
                    return (
                      <div
                        className={Styles.conditionGroupsContainer}
                        key={`conditionGroups-${groupIndex}-${conditionIndex}`}
                      >
                        <div className={Styles.conditionGroups}>
                          <JoinButton
                            glue={group.glue}
                            isWhere={conditionIndex === 0}
                            isButton={conditionIndex === 1}
                            onSelect={(glue) => updateGlue(glue, groupIndex, conditionIndex)}
                            isDisabled={isReadOnlyUser}
                          />
                          <div className={Styles.conditionGroupsList}>
                            <div className={Styles.groupControls}>
                              <div>All of the following are true</div>
                              <div>
                                <DropdownTrigger>
                                  <IconButton className={Styles.plusButton} variant="tertiary" icon={Add} />
                                  {(close) => (
                                    <div className={Styles.addConditionDropdown}>
                                      <Button
                                        onPress={() => {
                                          addCondition(groupIndex, conditionIndex)
                                          close()
                                        }}
                                        variant="tertiary"
                                      >
                                        Add condition
                                      </Button>
                                      <Button isDisabled={true} variant="tertiary">
                                        Add condition group
                                      </Button>
                                    </div>
                                  )}
                                </DropdownTrigger>
                                <RemoveConditionButton
                                  onPress={() => removeCondition(groupIndex, conditionIndex)}
                                  isDisabled={isReadOnlyUser}
                                />
                              </div>
                            </div>
                            {condition.conditions.filter(isCondition).map((nestedCondition, nestedConditionIndex) => (
                              <div
                                className={Styles.filter}
                                key={`${nestedCondition.operand.property_id}-${groupIndex}-${conditionIndex}-${nestedConditionIndex}`}
                              >
                                <JoinButton
                                  glue={condition.glue}
                                  isWhere={nestedConditionIndex === 0}
                                  isButton={nestedConditionIndex === 1}
                                  onSelect={(glue) =>
                                    updateGlue(glue, groupIndex, conditionIndex, nestedConditionIndex)
                                  }
                                  isDisabled={isReadOnlyUser}
                                />
                                <PropertyFilter
                                  condition={nestedCondition}
                                  onChange={(property: Property) =>
                                    changePropertyFilter(property, groupIndex, conditionIndex, nestedConditionIndex)
                                  }
                                />
                                <FilterOperators
                                  condition={nestedCondition}
                                  onChangeOperator={(operator) =>
                                    changeOperator(operator, groupIndex, conditionIndex, nestedConditionIndex)
                                  }
                                />
                                <ValueCollection
                                  condition={nestedCondition}
                                  updateValue={(value) =>
                                    updateValue(value, groupIndex, conditionIndex, nestedConditionIndex)
                                  }
                                  onChangeOperator={(operator) =>
                                    changeOperator(operator, groupIndex, conditionIndex, nestedConditionIndex)
                                  }
                                />
                                <RemoveConditionButton
                                  onPress={() => removeCondition(groupIndex, conditionIndex, nestedConditionIndex)}
                                  isDisabled={
                                    isReadOnlyUser || isCalendarPropertyId(nestedCondition.operand.property_id)
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          )
        })}
      </div>

      <div className={cx(Styles.addConditionsContainer, { [Styles.uneditable]: isReadOnlyUser })}>
        <Button
          className={Styles.addConditionButton}
          onPress={() => addCondition()}
          size="lg"
          variant="tertiary"
          frontIcon={Add}
          isDisabled={isReadOnlyUser}
        >
          Add condition
        </Button>
        {/*
        We are not supporting this just yet. There are some small bugs to work out.

        <Button onPress={() => addConditionGroup()} variant="secondaryGrey">
          Add condition group
        </Button>
        */}
      </div>
    </div>
  )
}
